import { databases } from '../config/appwrite';
import { Query } from 'appwrite';
import { Schedule, User } from '../types';
import { appwriteConfig } from '../config/appwrite';
import { ArticleService } from './articleService';
import { createGermanDateTimeFromParts, getGermanDateTime } from '../utils/dateTimeUtils';

export class ScheduleService {
  private static collectionId = appwriteConfig.collections.schedules;

  /**
   * Alle Termine abrufen
   */
  static async getAllSchedules(): Promise<Schedule[]> {
    try {
      const response = await databases.listDocuments(
        appwriteConfig.databaseId,
        this.collectionId,
        [Query.limit(100)]
      );

      return response.documents.map(doc => this.mapDocumentToSchedule(doc));
    } catch (error) {
      throw new Error('Fehler beim Laden der Termine');
    }
  }

  /**
   * Termine für eine bestimmte Plattform abrufen
   */
  static async getSchedulesByPlatform(platformId: string): Promise<Schedule[]> {
    try {
      const response = await databases.listDocuments(
        appwriteConfig.databaseId,
        this.collectionId,
        [
          Query.equal('platformId', platformId),
          Query.limit(100)
        ]
      );

      return response.documents.map(doc => this.mapDocumentToSchedule(doc));
    } catch (error) {
      throw new Error('Fehler beim Laden der Plattform-Termine');
    }
  }

  /**
   * Termine für einen bestimmten Autor abrufen
   */
  static async getSchedulesByAuthor(authorId: string): Promise<Schedule[]> {
    try {
      const response = await databases.listDocuments(
        appwriteConfig.databaseId,
        this.collectionId,
        [
          Query.equal('authorId', authorId),
          Query.limit(100)
        ]
      );

      return response.documents.map(doc => this.mapDocumentToSchedule(doc));
    } catch (error) {
      throw new Error('Fehler beim Laden der Autor-Termine');
    }
  }

  /**
   * Alle Autoren mit mindestens einem festen Termin auf einer Plattform abrufen
   */
  static async getAuthorsWithScheduleOnPlatform(platformId: string): Promise<User[]> {
    try {
      // 1. Alle Schedules für die Plattform laden
      const schedules = await this.getSchedulesByPlatform(platformId);
      
      // 2. Unique Author-IDs extrahieren
      const authorIdSet = new Set<string>();
      schedules.forEach(schedule => authorIdSet.add(schedule.authorId));
      const uniqueAuthorIds = Array.from(authorIdSet);
      
      // 3. User-Daten für alle Author-IDs laden (Batch-Query für Performance)
      const { AuthService } = await import('./authService');
      let authors: User[] = [];
      
      if (uniqueAuthorIds.length > 0) {
        try {
          authors = await AuthService.getUsersByIds(uniqueAuthorIds);
        } catch (error) {
          console.error('Fehler beim Batch-Laden der Autoren:', error);
          // Fallback: Leeres Array zurückgeben
          authors = [];
        }
      }
      
      // 4. Nach Name sortieren
      return authors.sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Fehler beim Laden der Autoren mit Schedules:', error);
      throw new Error('Fehler beim Laden der Autoren');
    }
  }

  /**
   * Termine für einen bestimmten Wochentag und eine Uhrzeit abrufen
   */
  static async getSchedulesByDayAndTime(dayOfWeek: number, time: string): Promise<Schedule[]> {
    try {
      const response = await databases.listDocuments(
        appwriteConfig.databaseId,
        this.collectionId,
        [
          Query.equal('dayOfWeek', dayOfWeek),
          Query.equal('time', time),
          Query.limit(100)
        ]
      );

      return response.documents.map(doc => this.mapDocumentToSchedule(doc));
    } catch (error) {
      throw new Error('Fehler beim Laden der Termine für Tag und Zeit');
    }
  }

  /**
   * Prüfen, ob ein Termin bereits belegt ist
   */
  static async isSlotOccupied(platformId: string, dayOfWeek: number, time: string): Promise<boolean> {
    try {
      const response = await databases.listDocuments(
        appwriteConfig.databaseId,
        this.collectionId,
        [
          Query.equal('platformId', platformId),
          Query.equal('dayOfWeek', dayOfWeek),
          Query.equal('time', time),
          Query.equal('isActive', true),
          Query.limit(1)
        ]
      );

      return response.documents.length > 0;
    } catch (error) {
      throw new Error('Fehler beim Prüfen der Termin-Belegung');
    }
  }

  /**
   * Nächsten freien Termin für eine Plattform finden
   */
  static async findNextAvailableSlot(platformId: string): Promise<{ dayOfWeek: number; time: string } | null> {
    try {
      // Alle aktiven Termine für die Plattform abrufen
      const existingSchedules = await this.getSchedulesByPlatform(platformId);
      const occupiedSlots = new Set(
        existingSchedules
          .filter(schedule => schedule.isActive)
          .map(schedule => `${schedule.dayOfWeek}-${schedule.time}`)
      );

      // Alle möglichen Zeitslots durchgehen (7 Tage × 18 Stunden, 6:00-23:00)
      for (let day = 0; day < 7; day++) {
        for (let hour = 6; hour <= 23; hour++) {
          const time = `${hour.toString().padStart(2, '0')}:00`;
          const slotKey = `${day}-${time}`;
          
          if (!occupiedSlots.has(slotKey)) {
            return { dayOfWeek: day, time };
          }
        }
      }

      return null; // Kein freier Slot gefunden
    } catch (error) {
      throw new Error('Fehler beim Finden des nächsten freien Termins');
    }
  }

  /**
   * Neuen Termin erstellen
   */
  static async createSchedule(scheduleData: Omit<Schedule, 'id' | 'createdAt' | 'updatedAt'>): Promise<Schedule> {
    try {
      // Prüfen, ob der Slot bereits belegt ist
      const isOccupied = await this.isSlotOccupied(
        scheduleData.platformId,
        scheduleData.dayOfWeek,
        scheduleData.time
      );

      if (isOccupied) {
        throw new Error('Dieser Termin ist bereits belegt');
      }

      const response = await databases.createDocument(
        appwriteConfig.databaseId,
        this.collectionId,
        'unique()',
        {
          platformId: scheduleData.platformId,
          authorId: scheduleData.authorId,
          dayOfWeek: scheduleData.dayOfWeek,
          time: scheduleData.time,
          isActive: scheduleData.isActive
        }
      );

      return this.mapDocumentToSchedule(response);
    } catch (error) {
      throw new Error('Fehler beim Erstellen des Termins');
    }
  }

  /**
   * Termin aktualisieren
   */
  static async updateSchedule(scheduleId: string, updates: Partial<Schedule>): Promise<Schedule> {
    try {
      const response = await databases.updateDocument(
        appwriteConfig.databaseId,
        this.collectionId,
        scheduleId,
        {
          ...(updates.platformId && { platformId: updates.platformId }),
          ...(updates.authorId && { authorId: updates.authorId }),
          ...(updates.dayOfWeek !== undefined && { dayOfWeek: updates.dayOfWeek }),
          ...(updates.time && { time: updates.time }),
          ...(updates.isActive !== undefined && { isActive: updates.isActive })
        }
      );

      return this.mapDocumentToSchedule(response);
    } catch (error) {
      throw new Error('Fehler beim Aktualisieren des Termins');
    }
  }

  /**
   * Termin löschen
   */
  static async deleteSchedule(scheduleId: string): Promise<void> {
    try {
      await databases.deleteDocument(
        appwriteConfig.databaseId,
        this.collectionId,
        scheduleId
      );
    } catch (error) {
      throw new Error('Fehler beim Löschen des Termins');
    }
  }

  /**
   * Appwrite Document zu Schedule Interface mappen
   */
  private static mapDocumentToSchedule(doc: any): Schedule {
    return {
      id: doc.$id,
      platformId: doc.platformId,
      authorId: doc.authorId,
      dayOfWeek: doc.dayOfWeek,
      time: doc.time,
      isActive: doc.isActive,
      createdAt: new Date(doc.$createdAt),
      updatedAt: new Date(doc.$updatedAt)
    };
  }

  /**
   * Wochentag-Namen abrufen (Montag = 0, Sonntag = 6)
   */
  static getDayName(dayOfWeek: number): string {
    const days = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
    return days[dayOfWeek] || 'Unbekannt';
  }

  /**
   * Alle verfügbaren Zeiten (6:00 bis 23:00)
   */
  static getAvailableTimes(): string[] {
    const times: string[] = [];
    for (let hour = 6; hour <= 23; hour++) {
      times.push(`${hour.toString().padStart(2, '0')}:00`);
    }
    return times;
  }

  /**
   * Sucht einen freien Slot, beginnend mit dem berechneten Datum
   */
  private static async findFreeSlotWithConflictCheck(
    authorId: string,
    platformId: string, 
    startDate: Date,
    schedule: Schedule,
    currentUser: User
  ): Promise<Date> {
    // WICHTIG: Zeit aus DB ist IMMER deutsche Zeit (Europe/Berlin)
    // Niemals lokale Browser-Zeitzone verwenden!
    const [scheduleHours, scheduleMinutes] = schedule.time.split(':').map(Number);
    
    for (let week = 0; week < 100; week++) {
      // Erstelle Datum-String im Format YYYY-MM-DD
      const testDate = new Date(startDate);
      testDate.setDate(startDate.getDate() + (week * 7));
      
      const germanDate = createGermanDateTimeFromParts({
        year: testDate.getFullYear(),
        month: testDate.getMonth() + 1,
        day: testDate.getDate(),
        hours: scheduleHours,
        minutes: scheduleMinutes
      });
      
      // Prüfe Konflikt mit ArticleService
      const hasConflict = await ArticleService.hasScheduleConflict(
        authorId,
        platformId, 
        germanDate,
        currentUser
      );
      
      if (!hasConflict) {
        return germanDate;
      }
    }
    
    // Fallback nach 100 Wochen
    return new Date(startDate);
  }

  /**
   * Findet den nächsten verfügbaren Veröffentlichungstermin für einen Autor
   * Berücksichtigt deutsche Zeitzone und gleichen Tag falls möglich
   */
  static async findNextAvailableSlotForAuthor(
    authorId: string, 
    platformId: string, 
    schedule: Schedule,
    currentUser: User
  ): Promise<Date> {
    const germanNow = getGermanDateTime();
    const currentDayOfWeek = germanNow.getDay(); // 0=Sonntag, 1=Montag, etc.
    const currentTime = germanNow.getHours() * 60 + germanNow.getMinutes();
    
    // Konvertiere Schedule dayOfWeek zu JavaScript Format
    let targetDayOfWeekJS;
    if (schedule.dayOfWeek === 6) { // Sonntag in unserem System
      targetDayOfWeekJS = 0; // Sonntag in JavaScript
    } else {
      targetDayOfWeekJS = schedule.dayOfWeek + 1; // +1 weil wir Montag=0 haben
    }
    
    // WICHTIG: Zeit aus DB ist IMMER deutsche Zeit, niemals umrechnen!
    const [scheduleHours, scheduleMinutes] = schedule.time.split(':').map(Number);
    const scheduleTime = scheduleHours * 60 + scheduleMinutes;
    
    let daysToAdd: number;
    
    if (currentDayOfWeek === targetDayOfWeekJS) {
      // Gleicher Wochentag: Prüfe ob noch Zeit ist
      if (currentTime < scheduleTime) {
        daysToAdd = 0; // Heute noch möglich
      } else {
        daysToAdd = 7; // Heute zu spät, nächste Woche
      }
    } else {
      // Anderer Wochentag: Berechne normal
      daysToAdd = targetDayOfWeekJS - currentDayOfWeek;
      if (daysToAdd <= 0) {
        daysToAdd += 7;
      }
    }
    
    // Erstelle Ziel-Datum mit expliziter deutscher Zeitzone
    const targetDate = new Date(germanNow);
    targetDate.setDate(germanNow.getDate() + daysToAdd);
    
    const germanDateTime = createGermanDateTimeFromParts({
      year: targetDate.getFullYear(),
      month: targetDate.getMonth() + 1,
      day: targetDate.getDate(),
      hours: scheduleHours,
      minutes: scheduleMinutes
    });
    
    // Prüfe auf Konflikte und suche freien Slot
    return await this.findFreeSlotWithConflictCheck(authorId, platformId, germanDateTime, schedule, currentUser);
  }
}
